/**
 * Yelp Metric + Dimension Catalogue (Story 23.5 AC #12)
 *
 * Ships the FULL 24-metric + 21-dimension Yelp Fusion catalogue (100% / 100%
 * coverage). Yelp has a small public API surface so there's no `gap` set
 * to defer — Story 23.5 ships everything in one pass.
 *
 * Story 23.6 (presets + fixtures) adds 2 NEW synthetic-aggregate metrics
 * (`ratingTrendDirection` + `reviewVelocityPerMonth`) bumping the catalogue
 * to 26/26. Those are NOT in this file; they live in Story 23.6.
 *
 * **Build-time test** in `metric-allowlist.test.ts` asserts every
 * `apiField` value is present in `allYelpFields()` from `./fields`.
 * Catches typos like `review_Count` (mixed-case) → `review_count`.
 */
import type { DimensionDefinition, MetricDefinition } from "../adapter";

export const YELP_METRICS: ReadonlyArray<MetricDefinition> = [
  // === Business Scorecard Group (10) ========================================
  {
    key: "businessRating",
    label: "Business Rating",
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { denominator: "reviewCount" },
    category: "reviews",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "rating",
    description:
      "Yelp's 1-5 star aggregate rating for this business (decimal — e.g. 4.3). The marquee Yelp metric.",
  },
  {
    key: "reviewCount",
    label: "Review Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "reviews",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "review_count",
    description: "Total number of reviews on this Yelp business listing.",
  },
  {
    key: "priceTier",
    label: "Price Tier",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["kpi", "data-table"],
    apiField: "price",
    description: "Yelp price indicator ($ to $$$$).",
  },
  {
    key: "isClosed",
    label: "Is Permanently Closed",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["kpi"],
    apiField: "is_closed",
    description: "True when the business listing is marked permanently closed.",
  },
  {
    key: "isClaimed",
    label: "Is Business Claimed",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["kpi"],
    apiField: "is_claimed",
    description: "True when the business owner has claimed the Yelp listing.",
  },
  {
    key: "phone",
    label: "Business Phone (raw)",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "phone",
    description: "Business phone number (E.164 format). PII-flagged.",
  },
  {
    key: "displayPhone",
    label: "Business Phone (display)",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "display_phone",
    description: "Human-formatted phone number. PII-flagged.",
  },
  {
    key: "businessName",
    label: "Business Name",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "name",
    description: "Business name as listed on Yelp.",
  },
  {
    key: "alias",
    label: "Business Alias",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "alias",
    description: "URL-safe slug identifier (e.g. 'greenlife-wellness-portland').",
  },
  {
    key: "yelpBusinessUrl",
    label: "Yelp Business URL",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "url",
    description: "Canonical Yelp business page URL.",
  },

  // === Business Media Group (2) =============================================
  {
    key: "primaryImageUrl",
    label: "Primary Image URL",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "image_url",
    description: "Primary business photo URL.",
  },
  {
    key: "photosArray",
    label: "Business Photos",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "photos",
    description: "Array of business photo URLs (Enterprise tier only — empty on lower tiers).",
  },

  // === Business Capability Group (1) ========================================
  {
    key: "transactionsSupported",
    label: "Transactions Supported",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "transactions",
    description:
      "Array of supported transaction types — e.g. 'delivery', 'pickup', 'restaurant_reservation'.",
  },

  // === Review Excerpts Group (5 — Plus+ tier) ===============================
  {
    key: "reviewRating",
    label: "Excerpt Rating",
    type: "number",
    dataType: "Integer",
    aggregationKind: "ratio",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "reviews[].rating",
    description: "Per-excerpt 1-5 star rating.",
  },
  {
    key: "reviewCreatedTime",
    label: "Excerpt Created Time",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "reviews[].time_created",
    description: "ISO 8601 timestamp when the review was created on Yelp.",
  },
  {
    key: "reviewExcerptText",
    label: "Excerpt Text",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "reviews[].text",
    description:
      "Review text excerpt (~160 char max — Yelp returns only excerpts, NEVER full text). PII-flagged.",
  },
  {
    key: "reviewUrl",
    label: "Excerpt URL",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "reviews[].url",
    description: "Direct Yelp link to the full review.",
  },
  {
    key: "reviewId",
    label: "Excerpt ID",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "reviews[].id",
    description: "Opaque Yelp review identifier.",
  },

  // === Review Aggregations Group (4 — derived) ==============================
  {
    key: "avgExcerptRating",
    label: "Average Excerpt Rating",
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { denominator: "totalExcerptsReturned" },
    category: "reviews",
    compatibleWidgets: ["kpi"],
    synthetic: true,
    apiField: "avgExcerptRating",
    description:
      "Mean of the 3-7 most recent review excerpt ratings (excerpts only — NOT full review history). NULL when zero reviews.",
  },
  {
    key: "possibleLanguagesCount",
    label: "Possible Languages Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "reviews",
    compatibleWidgets: ["kpi"],
    apiField: "reviews_response.possible_languages",
    description:
      "Count of distinct languages detected across the returned excerpts.",
  },
  {
    key: "totalExcerptsReturned",
    label: "Total Excerpts Returned",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "reviews",
    compatibleWidgets: ["kpi"],
    apiField: "reviews_response.total",
    description:
      "The count returned by the Yelp reviews endpoint — NOT the full review_count from the business details endpoint.",
  },
  {
    key: "ratingDelta",
    label: "Rating Delta (vs prior day)",
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio",
    category: "reviews",
    compatibleWidgets: ["kpi", "line-chart"],
    synthetic: true,
    apiField: "ratingDelta",
    description:
      "Today's rating minus yesterday's snapshot rating (from yelp_rating_snapshots). NULL when no prior snapshot exists.",
  },

  // === Trend Snapshots Group (2 — D1 snapshot history) ======================
  {
    key: "ratingHistory",
    label: "Rating History",
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { denominator: "reviewCountHistory" },
    category: "reviews",
    compatibleWidgets: ["line-chart"],
    synthetic: true,
    apiField: "ratingHistory",
    description:
      "Daily rating snapshot history from `yelp_rating_snapshots` D1 table — series for line-chart rendering.",
  },
  {
    key: "reviewCountHistory",
    label: "Review Count History",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "reviews",
    compatibleWidgets: ["line-chart"],
    synthetic: true,
    apiField: "reviewCountHistory",
    description:
      "Daily review_count snapshot history from `yelp_rating_snapshots` D1 table — series for line-chart rendering.",
  },

  // === Story 23.6 — Synthetic-Aggregate Metrics (2) ========================
  // Computed at preset-expansion time from the 45-day yelp_rating_snapshots
  // D1 window. NOT directly fetched from Yelp Fusion's REST API; the
  // metric-allowlist test exempts synthetic-true entries from the wire-
  // format allowlist check. Mirrors Moz 20.8's `domainAuthorityTrend` +
  // CallRail 22.2's `callsTrendDirection` + GR 23.2's
  // `reviewVelocityPerMonth` + Trustpilot 23.4's `reviewVelocityPerMonth`
  // precedent. The 2-count matches GR 23.2 + CallRail 22.2 + CTM 22.4 +
  // Moz 20.8 — NOT the Trustpilot 23.4 3-count because Yelp has no
  // equivalent Bayesian transparency wedge (ADR-C decision).
  {
    key: "ratingTrendDirection",
    label: "Rating Trend Direction",
    type: "number",
    dataType: "Integer",
    aggregationKind: "ratio",
    category: "reviews",
    compatibleWidgets: ["kpi"],
    synthetic: true,
    apiField: "<derived>",
    description:
      "1 = improving (second-half-mean > first-half-mean + 0.1), 0 = flat (within ±0.1), -1 = declining (second-half-mean < first-half-mean - 0.1). Computed from the 45-day yelp_rating_snapshots window. Story 23.6 Reputation Scorecard 4th-slot KPI (ADR-C platform-specific wedge).",
  },
  {
    key: "reviewVelocityPerMonth",
    label: "Review Velocity (per month)",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "reviews",
    compatibleWidgets: ["kpi"],
    synthetic: true,
    apiField: "<derived>",
    description:
      "Rolling 30-day review count — pace metric. Computed from yelp_rating_snapshots delta between newest and 30-days-prior snapshot's reviewCount field. Surfaces 'this listing gets ~5 new Yelp reviews/month' as a single Scorecard KPI.",
  },
];

export const YELP_DIMENSIONS: ReadonlyArray<DimensionDefinition> = [
  // === Identity (6) =========================================================
  { key: "businessId", label: "Business ID", type: "Structure" },
  { key: "businessAlias", label: "Business Alias", type: "Structure" },
  { key: "businessName", label: "Business Name", type: "Structure" },
  { key: "yelpUrl", label: "Yelp URL", type: "Structure" },
  { key: "phone", label: "Phone", type: "Structure", exampleValues: ["+15555550100"] },
  {
    key: "displayPhone",
    label: "Phone (display)",
    type: "Structure",
    exampleValues: ["(555) 555-0100"],
  },

  // === Categories (2) =======================================================
  { key: "categoryAlias", label: "Category Alias", type: "Classification" },
  { key: "categoryTitle", label: "Category Title", type: "Classification" },

  // === Pricing (1) ==========================================================
  { key: "priceTier", label: "Price Tier", type: "Classification", exampleValues: ["$", "$$", "$$$", "$$$$"] },

  // === Location (5) =========================================================
  { key: "locationCity", label: "City", type: "Geo" },
  { key: "locationState", label: "State", type: "Geo" },
  { key: "locationZipCode", label: "Zip Code", type: "Geo" },
  { key: "locationCountry", label: "Country", type: "Geo", exampleValues: ["US", "CA"] },
  { key: "coordinates", label: "Coordinates", type: "Geo" },

  // === Review per-excerpt (4) ===============================================
  { key: "reviewDate", label: "Review Date", type: "Time" },
  { key: "reviewRating", label: "Review Rating", type: "Review", exampleValues: ["1", "2", "3", "4", "5"] },
  { key: "reviewerName", label: "Reviewer Name", type: "Review" },
  { key: "reviewerId", label: "Reviewer ID", type: "Review" },

  // === Reviewer Profile (2) =================================================
  { key: "reviewerProfileUrl", label: "Reviewer Profile URL", type: "Review" },
  { key: "reviewerImageUrl", label: "Reviewer Image URL", type: "Review" },

  // === Language (1) =========================================================
  { key: "possibleReviewLanguages", label: "Possible Review Languages", type: "Content" },
];
